/**
 * Facility Settings
 * 
 * Settings panel for ChartMind admin facility/organization details.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { contentCardSx } from '../../../../../styles/standaloneLayoutTokens';
import SaveIcon from '@mui/icons-material/Save';

const FacilitySettings = ({
  facilityName = '',
  organizationName = '',
  outboundCallerId = '',
  onSave,
  isSaving = false,
}) => {
  const [localFacility, setLocalFacility] = useState(facilityName);
  const [localOrg, setLocalOrg] = useState(organizationName);
  const [localOutboundCallerId, setLocalOutboundCallerId] = useState(outboundCallerId);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalFacility(facilityName);
    setLocalOrg(organizationName);
    setLocalOutboundCallerId(outboundCallerId);
  }, [facilityName, organizationName, outboundCallerId]);

  useEffect(() => {
    setHasChanges(
      localFacility !== facilityName ||
      localOrg !== organizationName ||
      localOutboundCallerId !== outboundCallerId
    );
  }, [localFacility, localOrg, localOutboundCallerId, facilityName, organizationName, outboundCallerId]);

  const handleSave = () => {
    onSave({
      facilityName: localFacility,
      organizationName: localOrg,
      outboundCallerId: localOutboundCallerId,
    });
  };

  return (
    <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Organization Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure your facility and organization details. This information will be displayed
        to your team members when they select their working organization.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        At least one of these fields is required for your team to identify your organization.
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label="Facility Name"
          value={localFacility}
          onChange={(e) => setLocalFacility(e.target.value)}
          fullWidth
          placeholder="e.g., Main Street Medical Center"
          helperText="The name of your clinic, hospital, or medical facility"
        />

        <TextField
          label="Organization Name"
          value={localOrg}
          onChange={(e) => setLocalOrg(e.target.value)}
          fullWidth
          placeholder="e.g., HealthCare Partners Network"
          helperText="The parent organization or healthcare network (if applicable)"
        />

        <TextField
          label="Outbound caller ID (for Dialer)"
          value={localOutboundCallerId}
          onChange={(e) => setLocalOutboundCallerId(e.target.value)}
          fullWidth
          placeholder="e.g., +1 555 123 4567"
          helperText="Clinic or office number shown as caller ID when your team uses the ChartMind Dialer. Team members (professional-chartmind) will see this number and cannot change it."
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default FacilitySettings;
